import { AxiosResponse } from 'axios';

import interceptor from '../common/interceptor';
import { APIPaths } from '../common/constants';
import type {
  CompanyCourierAccount,
  EmailLabelItem,
  QuotePiece,
  ShipResponsePiece,
} from './fedexQuote';
// Re-export so callers that import from upsQuote don't need a separate fedexQuote import.
export type { CompanyCourierAccount } from './fedexQuote';

export interface UPSParty {
  /** Contact person name → UPS AttentionName. */
  name?: string;
  /** Company / site name → UPS Name. */
  company?: string;
  phone?: string;
  street?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  residential?: boolean;
}

export interface UPSQuotePayload {
  shipper: UPSParty;
  recipient: UPSParty;
  /** Flat single-package fields; omitted when `pieces` is sent. */
  weight?: number;
  weightUnit?: 'LB' | 'KG';
  /** 2 to 40 boxes; replaces the flat weight and dimension fields. */
  pieces?: QuotePiece[];
  shipDate?: string;
  dryIceKg?: number;
  /** Non-corrugated packaging → AdditionalHandlingIndicator. */
  nonStandardPackaging?: boolean;
  /** Lithium batteries (lightly regulated HazMat). Requires lithiumBatteryMaterial. */
  lithiumBatteries?: boolean;
  lithiumBatteryMaterial?: 'LITHIUM_ION' | 'LITHIUM_METAL' | 'BOTH';
  reference?: string;
  commodityDescription?: string;
  commodityValue?: number;
  /** Optional package dimensions — sent only when all three are set. */
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: 'IN' | 'CM';
  /** Selected account number — sent to backend to override the default. */
  accountNumber?: string;
  /** Third-party UPS account to bill. */
  thirdPartyAccountNumber?: string;
  /** Postal code of the third-party account. */
  thirdPartyPostalCode?: string;
  /** When true on quote, price as Print Return Label (code 9). */
  returnLabel?: boolean;
  /** Outbound order/reference for RMA on return labels. */
  rmaReference?: string;
}

export interface UPSShipPayload extends UPSQuotePayload {
  serviceType: string;
  /** Proceed although the same booking was made recently or its outcome is unknown. */
  confirmDuplicate?: boolean;
}

export interface UPSRate {
  /** Unique within a quote response (serviceType alone can repeat). */
  rateId?: string;
  serviceType: string;
  serviceName?: string;
  serviceDescription?: string;
  currency?: string;
  totalNetCharge?: number;
  totalBaseCharge?: number;
  deliveryDate?: string | null;
  deliveryDay?: string | null;
  deliveryLabel?: string | null;
  transitTime?: string;
}

export interface UPSQuoteWarning {
  code?: string;
  message: string;
}

export interface UPSQuoteResponseData {
  rates: UPSRate[];
  raw?: unknown;
  /** Present when lithium/dry-ice dual-shop loaded only one transport mode. */
  warnings?: UPSQuoteWarning[];
}

export interface UPSShipResponseData {
  trackingNumber?: string | null;
  masterTrackingNumber?: string | null;
  /** One entry per box when the shipment has more than one. */
  pieces?: ShipResponsePiece[];
  serviceType?: string | null;
  serviceCategory?: string | null;
  labelBase64?: string | null;
  labelContentType?: string | null;
  labelUrl?: string | null;
  labelFetchError?: string | null;
  isVirtualResponse?: boolean;
  alerts?: Array<{ code?: string; message?: string }>;
  shipDate?: string | null;
  deliveryDate?: string | null;
  deliveryDay?: string | null;
  transitTime?: string | null;
  raw?: unknown;
}

interface CustomResponseEnvelope<T> {
  data: T;
  is_success: boolean;
  message?: string;
  status?: number;
}

/**
 * ZoomLogi HTTP bodies use `return_class`: axios `data.data` is the CustomResponse
 * row, and the real payload is nested again at `data.data.data`.
 */
export function unwrapUPSSuccessPayload<T extends object>(
  res: AxiosResponse<unknown>
): T | undefined {
  const body = res?.data as Record<string, unknown> | undefined;
  const envelope = body?.data as Record<string, unknown> | undefined;
  if (!envelope) return undefined;
  const nested = envelope.data;
  if (nested !== undefined && nested !== null && typeof nested === 'object') {
    return nested as T;
  }
  // The envelope itself is the payload when the backend does not nest it again.
  return envelope as T;
}

export function parseUPSQuoteResponse(
  res: AxiosResponse<unknown>
): UPSQuoteResponseData | undefined {
  const payload = unwrapUPSSuccessPayload<UPSQuoteResponseData>(res);
  if (payload?.rates && Array.isArray(payload.rates)) {
    return payload;
  }
  return undefined;
}

export function parseUPSShipResponse(
  res: AxiosResponse<unknown>
): UPSShipResponseData | undefined {
  const payload = unwrapUPSSuccessPayload<UPSShipResponseData>(res);
  if (!payload || typeof payload !== 'object') return undefined;
  return payload;
}

export const GetUPSAccounts = (
  token: string
): Promise<
  AxiosResponse<CustomResponseEnvelope<{ accounts: CompanyCourierAccount[] }>>
> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosGet('Get', APIPaths.upsAccounts, token)
      .then((res) =>
        resolve(
          res as AxiosResponse<
            CustomResponseEnvelope<{ accounts: CompanyCourierAccount[] }>
          >
        )
      )
      .catch((error) => reject(error?.response));
  });
};

export const RequestUPSQuote = (
  token: string,
  payload: UPSQuotePayload
): Promise<AxiosResponse<CustomResponseEnvelope<UPSQuoteResponseData>>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.upsQuote, payload, token)
      .then((res) =>
        resolve(
          res as AxiosResponse<CustomResponseEnvelope<UPSQuoteResponseData>>
        )
      )
      .catch((error) => reject(error?.response));
  });
};

export const BookUPSShipment = (
  token: string,
  payload: UPSShipPayload,
  idempotencyKey?: string
): Promise<AxiosResponse<CustomResponseEnvelope<UPSShipResponseData>>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost(
        'Post',
        APIPaths.upsShip,
        payload,
        token,
        undefined,
        undefined,
        // The drawer shows booking failures (409 duplicate, in progress, unknown) inline, so no global toast; a 401 still ends the session.
        'keep-auth',
        idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined
      )
      .then((res) =>
        resolve(
          res as AxiosResponse<CustomResponseEnvelope<UPSShipResponseData>>
        )
      )
      // A request that got no response (timeout, dropped connection) has an unknown outcome.
      .catch((error) => reject(error?.response ?? error));
  });
};

export interface UPSEmailLabelPayload {
  to_email: string;
  label_base64?: string;
  /** One attachment per box; replaces `label_base64` for multi-box shipments. */
  labels?: EmailLabelItem[];
  tracking_number?: string;
  reference?: string;
  content_type?: string;
}

export const EmailUPSLabel = (
  token: string,
  payload: UPSEmailLabelPayload
): Promise<AxiosResponse<CustomResponseEnvelope<{ sent: boolean }>>> => {
  return new Promise((resolve, reject) => {
    interceptor
      .axiosPost('Post', APIPaths.upsEmailLabel, payload, token)
      .then((res) =>
        resolve(res as AxiosResponse<CustomResponseEnvelope<{ sent: boolean }>>)
      )
      .catch((error) => reject(error?.response));
  });
};
